import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { CONVERA_FILE_TYPE, DIRECTION } from 'components/constants';
import i18n from 'i18n';
import { downloadConveraStatementByIdRequest, getConveraStatementsListRequest } from 'services/requestAgent';
import { convertDate, convertToFullDate } from 'services/utils';

class ConveraStatementsStore {
  isInitialized = false;
  isLoading = false;
  error = null;
  converaStatementsList = [];
  totalElements = null;
  sortDirection = DIRECTION.DESC;
  pagination = {
    size: 20,
    page: 0,
    totalPages: null
  };
  filters = {
    createdFrom: null,
    createdTo: null,
    type: [],
    filename: ''
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetConveraStatementsStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.error = null;
    this.converaStatementsList = [];
    this.totalElements = null;
    this.sortDirection = DIRECTION.DESC;
    this.pagination = {
      size: 20,
      page: 0,
      totalPages: null
    };
    this.filters = {
      createdFrom: null,
      createdTo: null,
      type: [],
      filename: ''
    };
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  setPageNumber = (page) => {
    this.pagination.page = page;
  };

  setPageSize = (size) => {
    this.pagination.size = size;
  };

  setFilter = (fieldName, value) => {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  };

  setSortDirection = (direction) => {
    this.sortDirection = direction;
  };

  setFiltersFromUrl = (params) => {
    const filters = { ...this.filters };
    const pagination = { ...this.pagination };

    // The URL holds the request values (e.g. 2026-07-27T23:59:59Z, see prepareFiltersParams) - only the date part
    // is taken, so the picked day doesn't shift with the browser time zone
    if (params.created_from) {
      filters.createdFrom = convertToFullDate(params.created_from.split('T')[0]);
    }
    if (params.created_to) {
      filters.createdTo = convertToFullDate(params.created_to.split('T')[0]);
    }
    if (params.type) {
      // query-string returns a plain string for a single value and an array for repeated ones.
      // Unknown types are dropped: the backend rejects them with 400 and the page would never load
      filters.type = [].concat(params.type).filter((type) => Object.values(CONVERA_FILE_TYPE).includes(type));
    }
    if (params.filename) {
      filters.filename = params.filename;
    }

    pagination.page = parseInt(params.page) || pagination.page;
    pagination.size = parseInt(params.size) || pagination.size;

    this.sortDirection = params.sort_direction || this.sortDirection;
    this.filters = filters;
    this.pagination = pagination;
  };

  prepareFiltersParams = () => {
    const params = {};

    // The connector proxy expects a full ISO date-time with a UTC designator (e.g. 2026-07-27T00:00:00Z),
    // not a bare date or a zone-less date-time - otherwise the request fails with ARGUMENT_TYPE_MISMATCH.
    if (this.filters.createdFrom) {
      params.created_from = `${convertDate(this.filters.createdFrom)}T00:00:00Z`;
    }
    if (this.filters.createdTo) {
      params.created_to = `${convertDate(this.filters.createdTo)}T23:59:59Z`;
    }
    // Sent as a repeated `type` param; omitted when empty so files of all types (including untyped) are returned
    if (this.filters.type.length) {
      params.type = this.filters.type;
    }
    // Passed to the backend as entered (no trimming) - matching is up to the connector
    if (this.filters.filename) {
      params.filename = this.filters.filename;
    }

    return params;
  };

  getConveraStatementsList = async () => {
    this.setIsLoading(true);
    try {
      const filtersParams = this.prepareFiltersParams();

      const res = await getConveraStatementsListRequest(this.pagination, this.sortDirection, filtersParams);

      runInAction(() => {
        this.isInitialized = true;
        this.converaStatementsList = res.content;
        this.totalElements = res.total_elements;
        this.pagination = {
          size: res.size,
          page: res.number,
          totalPages: res.total_pages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  downloadConveraStatementById = async (fileId) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(downloadConveraStatementByIdRequest(fileId), {
      pending: i18n.getMessage('converaStatementsStore.downloadConveraStatementById.toastPromise.pending'),
      success: i18n.getMessage('converaStatementsStore.downloadConveraStatementById.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };
}

export default new ConveraStatementsStore();
